import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import { LegalDocId } from '../features/legal/legalTypes';
import { DayRoute } from '../features/day/DayRoute';
import { PrintMode } from '../features/print/printMode';
import { Shell } from '../shell/Shell';
import { RootRedirect } from './RootRedirect';
import { RoutePattern } from './routes';

const Onboarding = lazy(() => import('../features/onboarding/Onboarding'));
// The week view (with its summary row and print menu) is only needed once the user opens it.
const WeekRoute = lazy(() =>
  import('../features/week/WeekRoute').then((module) => ({ default: module.WeekRoute })),
);
const VerifyEmailPage = lazy(() => import('../features/sync/VerifyEmailPage'));
const LegalPage = lazy(() => import('../features/legal/LegalPage'));
const PrintRoute = lazy(() => import('../features/print/PrintRoute'));
const MonthRoute = lazy(() =>
  import('../features/month/MonthRoute').then((module) => ({ default: module.MonthRoute })),
);
// Statistics and settings are secondary views: keep them out of the first-load script.
const StatsPage = lazy(() => import('../features/stats/StatsPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));

/** The route table: the shell wraps every view except the first-run screen. */
export function AppRoutes() {
  return (
    <Routes>
      <Route
        path={RoutePattern.Onboarding}
        element={
          <Suspense fallback={null}>
            <Onboarding />
          </Suspense>
        }
      />
      {/* Opened from mails and the registration form, with or without an account: no shell, no welcome gate. */}
      <Route
        path={RoutePattern.VerifyEmail}
        element={
          <Suspense fallback={null}>
            <VerifyEmailPage />
          </Suspense>
        }
      />
      <Route
        path={RoutePattern.LegalPrivacy}
        element={
          <Suspense fallback={null}>
            <LegalPage id={LegalDocId.Privacy} />
          </Suspense>
        }
      />
      <Route
        path={RoutePattern.LegalTerms}
        element={
          <Suspense fallback={null}>
            <LegalPage id={LegalDocId.Terms} />
          </Suspense>
        }
      />
      {/* Print sheets have no app chrome: they sit outside the shell. */}
      <Route
        path={RoutePattern.PrintBlank}
        element={
          <Suspense fallback={null}>
            <PrintRoute mode={PrintMode.Blank} />
          </Suspense>
        }
      />
      <Route
        path={RoutePattern.PrintWeek}
        element={
          <Suspense fallback={null}>
            <PrintRoute mode={PrintMode.Filled} />
          </Suspense>
        }
      />
      <Route path="/" element={<Shell />}>
        <Route index element={<RootRedirect />} />
        <Route path="day/:date" element={<DayRoute />} />
        <Route
          path="week/:weekStart"
          element={
            <Suspense fallback={null}>
              <WeekRoute />
            </Suspense>
          }
        />
        <Route
          path="month/:month"
          element={
            <Suspense fallback={null}>
              <MonthRoute />
            </Suspense>
          }
        />
        <Route
          path="stats"
          element={
            <Suspense fallback={null}>
              <StatsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/:section?"
          element={
            <Suspense fallback={null}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route path="*" element={<RootRedirect />} />
      </Route>
    </Routes>
  );
}
